import React from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import {
    SwanProvider,
    SiteContent,
    SiteMain,
    MainContentStart,
    BoundedContent,
    ScreenClassProvider,
    SwanHead,
    SWAN_STYLE_KEY_MAP,
    useBrowserClasses,
    getRootClassNames,
} from '@vp/swan';
import {
    Header,
    Footer,
    BookendsMetaTag,
    BOOKEND_TYPES,
} from '@vp/esi-bookends';

import { getCurrentLocale } from './utils/locale';

const styleSheetKeys = [
    SWAN_STYLE_KEY_MAP.core,
    SWAN_STYLE_KEY_MAP.button,
    SWAN_STYLE_KEY_MAP.modalDialog,
    SWAN_STYLE_KEY_MAP.grid, // this is used by the consent manager and bookends
    SWAN_STYLE_KEY_MAP.popover,
    SWAN_STYLE_KEY_MAP.accordion,
    SWAN_STYLE_KEY_MAP.icon,
    SWAN_STYLE_KEY_MAP.hidden,
    SWAN_STYLE_KEY_MAP.visible,
    SWAN_STYLE_KEY_MAP.toggleSwitch, // Bookends dependencies
    SWAN_STYLE_KEY_MAP.standardTile,
    SWAN_STYLE_KEY_MAP.progressiveImage,
    SWAN_STYLE_KEY_MAP.utility,
    SWAN_STYLE_KEY_MAP.spinner,
];

const App = ({ pageName, children }) => {
    const trackingConfiguration = {
        pageSection: pageName,
        pageStage: pageName,
        pageName,
    };
    const locale = getCurrentLocale();
    const htmlClassName = useBrowserClasses();
    const bodyClassName = getRootClassNames();

    return (
        <HelmetProvider>
            <SwanProvider swanTenant="vistaprint" swanLocale={locale}>
                <SwanHead
                    styleSheetKeys={styleSheetKeys}
                    renderWith={Helmet}
                    renderStyleContentAsChildren
                />
                <BookendsMetaTag
                    renderWith={Helmet}
                    locale={locale}
                    tenant="vistaprint"
                    headerType={BOOKEND_TYPES.SLIM}
                    footerType={BOOKEND_TYPES.FULL}
                />
                <Helmet htmlAttributes={{ class: htmlClassName }} />
                <div className={bodyClassName}>
                    <ScreenClassProvider>
                        <SiteContent>
                            <Header
                                requestor="AILS"
                                locale={locale}
                                tenant="vistaprint"
                                hideSearch={true}
                                trackingConfiguration={trackingConfiguration}
                                type={BOOKEND_TYPES.SLIM}
                            />
                            <SiteMain>
                                <MainContentStart /> {/* for accessibility support */}
                                {/* unbounded page content does not need a BoundedContent wrapper */}
                                <BoundedContent>{children}</BoundedContent>
                            </SiteMain>
                            <Footer
                                requestor="AILS"
                                tenant="vistaprint"
                                locale={locale}
                                type={BOOKEND_TYPES.FULL}
                                trackingConfiguration={trackingConfiguration}
                            />
                        </SiteContent>
                    </ScreenClassProvider>
                </div>
            </SwanProvider>
        </HelmetProvider>
    );
};

export default App;
